import { List, Show, Create, Edit, SimpleForm, TextInput, SelectInput, Filter, SearchInput, useGetList } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, Chip, Alert, Grid, Avatar, Table, TableBody, TableCell, TableHead, TableRow, IconButton, useMediaQuery, TablePagination } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';

interface EEProfileItem {
  id?: string;
  reportingYear?: number;
  status?: string;
  lastUpdatedAt?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  profileData?: any;
  [key: string]: unknown;
}
import { Link, useNavigate } from 'react-router-dom';
import { PieChart, BarChart } from '@mui/x-charts-pro';
import { Group, Person, Close } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';

const STATUS_COLORS: Record<string, string> = { Draft: 'default', InReview: 'warning', Submitted: 'info', Active: 'success', Archived: 'default' };
const RACE_COLORS: Record<string, string> = {
  African: '#1976d2',
  Coloured: '#ed6c02',
  Indian: '#2e7d32',
  White: '#9c27b0',
};

const OCCUPATIONAL_LEVELS = ['TopManagement', 'SeniorManagement', 'Professional', 'SkilledTechnical', 'SemiSkilled', 'Unskilled'];
const formatLevel = (l: string) => l?.replace(/([A-Z])/g, ' $1').trim() || l;

export const EEProfileList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: profiles, isLoading } = useGetList('ee-profiles', { pagination: { page: 1, perPage: 100 } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const sorted = [...(profiles || [])].sort((a: EEProfileItem, b: EEProfileItem) => (b.reportingYear || 0) - (a.reportingYear || 0));
  const latest = sorted[0];
  const latestData = latest?.profileData;

  const stats = {
    totalEmployees: latestData?.totalEmployees ?? 0,
    male: latestData?.byGender?.Male ?? 0,
    female: latestData?.byGender?.Female ?? 0,
    disabled: latestData?.withDisability ?? 0,
  };
  const malePct = stats.totalEmployees > 0 ? Math.round((stats.male / stats.totalEmployees) * 100) : 0;
  const femalePct = stats.totalEmployees > 0 ? Math.round((stats.female / stats.totalEmployees) * 100) : 0;

  const raceEntries = latestData?.byRace ? Object.entries(latestData.byRace) : [];
  const pieData = raceEntries.map(([label, value]) => ({ label, value: value as number }));

  const levelData = OCCUPATIONAL_LEVELS.map(l => latestData?.byOccupationalLevel?.[l] ?? 0);
  const levelLabels = OCCUPATIONAL_LEVELS.map(formatLevel);

  const pageProfiles = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.employment-equity" wizardPath="wizard/ee-report" label="EE Report Wizard" />
        </Box>
        <ListPageHeader
          title="Employment Equity Profiles"
          metrics={[
            { label: 'Male', value: `${malePct}%`, color: 'primary.main' },
            { label: 'Female', value: `${femalePct}%`, color: 'warning.main' },
            { label: 'Disabled', value: String(stats.disabled), color: 'info.main' },
            { label: 'Total', value: String(stats.totalEmployees), color: 'success.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {sorted.map((profile: EEProfileItem) => (
            <Paper key={profile.id} sx={{ p: 2 }} onClick={() => navigate(`/ee-profiles/${profile.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>EE Profile — {profile.reportingYear}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {profile.profileData?.totalEmployees ?? 0} employees
                  </Typography>
                </Box>
                <Chip label={profile.status ?? '—'} size="small" color={((profile.status && STATUS_COLORS[profile.status]) || 'default') as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                <Chip size="small" label={`M: ${profile.profileData?.byGender?.Male ?? 0}`} />
                <Chip size="small" label={`F: ${profile.profileData?.byGender?.Female ?? 0}`} />
                <Chip size="small" label={`Disabled: ${profile.profileData?.withDisability ?? 0}`} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.employment-equity" wizardPath="wizard/ee-report" label="EE Report Wizard" />
      </Box>
      <ListPageHeader
        title="Employment Equity Profiles"
        metrics={[
          { label: 'Male', value: `${malePct}%`, color: 'primary.main' },
          { label: 'Female', value: `${femalePct}%`, color: 'warning.main' },
          { label: 'Disabled', value: String(stats.disabled), color: 'info.main' },
          { label: 'Total', value: String(stats.totalEmployees), color: 'success.main' },
        ]}
        pieChartData={pieData.length > 0 ? pieData : undefined}
        pieChartTitle="Race Distribution"
        barChartData={levelData}
        barChartLabels={levelLabels}
        barChartTitle="By Occupational Level"
      />

      {latest && (
        <Paper sx={{ mt: 2, p: 2.5, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Latest Profile — {latest.reportingYear}
          </Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Reporting Year', value: latest.reportingYear },
              { label: 'Status', value: latest.status },
              { label: 'Total Employees', value: stats.totalEmployees },
              { label: 'Disability Representation', value: stats.disabled },
              { label: 'Male / Female', value: `${stats.male} / ${stats.female}` },
              { label: 'Last Updated', value: latest.lastUpdatedAt ? new Date(latest.lastUpdatedAt).toLocaleDateString('en-GB') : '—' },
            ].map(f => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={f.label}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                <Typography variant="body2" fontWeight={600}>{f.value ?? '—'}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Male</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Female</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Disabled</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>African</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Coloured</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Indian</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>White</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageProfiles.map((profile: EEProfileItem) => {
              const pd = profile.profileData;
              return (
                <TableRow
                  key={profile.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/ee-profiles/${profile.id}/show`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{profile.reportingYear}</Typography>
                  </TableCell>
                  <TableCell>
                <Chip label={profile.status} size="small" color={(STATUS_COLORS[profile.status ?? ''] || 'default') as ChipColor} />
                  </TableCell>
                  <TableCell>{pd?.totalEmployees ?? 0}</TableCell>
                  <TableCell>{pd?.byGender?.Male ?? 0}</TableCell>
                  <TableCell>{pd?.byGender?.Female ?? 0}</TableCell>
                  <TableCell>{pd?.withDisability ?? 0}</TableCell>
                  <TableCell>{pd?.byRace?.African ?? 0}</TableCell>
                  <TableCell>{pd?.byRace?.Coloured ?? 0}</TableCell>
                  <TableCell>{pd?.byRace?.Indian ?? 0}</TableCell>
                  <TableCell>{pd?.byRace?.White ?? 0}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {profile.lastUpdatedAt ? new Date(profile.lastUpdatedAt).toLocaleDateString('en-GB') : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!sorted || sorted.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Group sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No EE profiles found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const EEProfileFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search by year or status" />
  </Filter>
);

export const EEProfileShow = () => {
  const navigate = useNavigate();
  return (
    <Show render={({ record }) => {
      if (!record) return null;
      const pd = record.profileData;
      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/ee-profiles')} size="small" sx={{ color: 'text.secondary' }}>
              <Close />
            </IconButton>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Employment Equity reporting for {record.reportingYear} — EEA2 Workforce Profile
          </Alert>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Workforce Profile — {record.reportingYear}</Typography>
            <Grid container spacing={2}>
              {[
                { label: 'Status', value: record.status },
                { label: 'Total Employees', value: pd?.totalEmployees ?? 0 },
                { label: 'Male', value: pd?.byGender?.Male ?? 0 },
                { label: 'Female', value: pd?.byGender?.Female ?? 0 },
                { label: 'Disability Representation', value: pd?.withDisability ?? 0 },
                { label: 'Last Updated', value: record.lastUpdatedAt ? new Date(record.lastUpdatedAt).toLocaleDateString('en-GB') : '—' },
              ].map(f => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={f.label}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>{f.value ?? '—'}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Race Distribution</Typography>
                {pd?.byRace ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {Object.entries(pd.byRace).map(([race, count]) => (
                      <Box key={race} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: RACE_COLORS[race] || 'grey.500' }} />
                        <Typography variant="body2">{race}: <strong>{count as number}</strong></Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No race data</Typography>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Occupational Levels</Typography>
                {pd?.byOccupationalLevel ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Object.entries(pd.byOccupationalLevel).map(([level, count]) => (
                      <Box key={level} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">{formatLevel(level)}</Typography>
                        <Chip label={count as number} size="small" />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No occupational level data</Typography>
                )}
              </Paper>
            </Grid>

            {record.incomeDifferential && (
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Income Differential</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Occupational Level</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatLevel(record.incomeDifferential.occupationalLevel)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Average Basic Salary</Typography>
                      <Typography variant="body2" fontWeight={600}>R {record.incomeDifferential.averageBasicSalary?.toLocaleString()}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Median Basic Salary</Typography>
                      <Typography variant="body2" fontWeight={600}>R {record.incomeDifferential.medianBasicSalary?.toLocaleString()}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Gender Ratio (M/F)</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {record.incomeDifferential.genderRatio?.Male ?? 0} / {record.incomeDifferential.genderRatio?.Female ?? 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}
          </Grid>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 3 }}>
            <Box component={Link} to="/employees" sx={{ textDecoration: 'none' }}>
              <Chip label="View Employees" clickable variant="outlined" />
            </Box>
            <Box component={Link} to="/terminations" sx={{ textDecoration: 'none' }}>
              <Chip label="View Terminations" clickable variant="outlined" />
            </Box>
            <Box component={Link} to="/disciplinary-cases" sx={{ textDecoration: 'none' }}>
              <Chip label="View Disciplinary" clickable variant="outlined" />
            </Box>
          </Box>
        </Box>
      );
    }} />
  );
};

export const EEProfileCreate = () => (
  <Create>
    <SimpleForm>
      <Typography variant="h6" sx={{ mb: 1 }}>EE Profile Details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
        <TextInput source="reportingYear" label="Reporting Year" fullWidth defaultValue="2026" />
      </Box>
    </SimpleForm>
  </Create>
);

export const EEProfileEdit = () => (
  <Edit>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="reportingYear" label="Reporting Year" fullWidth />
      </Box>
    </SimpleForm>
  </Edit>
);
